import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../../../../../store/utility';
import { backend_server_address } from '../../../../../backendServer';

// Status values as defined in API documentation
export type ODMStatus = 'pending' | 'accepted' | 'rejected' | 'timed_out' | 'timed_out_after_accepted' | 'completed';

// Error codes returned by the API
export type ODMError = null | 'machine_off' | 'busy';

export interface ODMRecord {
    id: number;
    point: number; // measurement_point_id
    source: 'manual' | 'gateway_button';
    status: ODMStatus;
    error: ODMError;
    timestamp: string;
    accepted_timestamp: string | null;
    rejected_timestamp: string | null;
    timed_out_timestamp: string | null;
    completed_timestamp: string | null;
    measurement_timestamp: string | null;
    published_at: string | null;
    user: {
        username: string;
        full_name: string;
    } | null;
}

// Invalid point details from POST response
export interface ODMInvalidPoint {
    code: string;
    detail: string; // Translated error message
    attr: string | null;
    point: number;
}

// POST response structure
export interface ODMCreateResponse {
    valid: ODMRecord[];
    invalid: ODMInvalidPoint[];
}

// Query params for GET /api/odm/
export interface GetODMsParams {
    node?: number;
    start_date?: string; // ISO format datetime
    end_date?: string; // ISO format datetime
    id?: number | number[]; // Single ID or array of IDs for filtering specific ODMs
}

export const odmAPI = createApi({
    reducerPath: 'odmAPI',
    baseQuery: axiosBaseQuery({ baseUrl: backend_server_address + '/api/odm/', arrayParamsSerializer: true }),
    tagTypes: ['ODMRecords'],
    endpoints: (builder) => ({
        // POST /api/odm/?node={nodeId} - Create ODM records for all measurement points
        // Returns 200 (all valid), 207 (partial), or 400 (all invalid/error)
        createODM: builder.mutation<ODMCreateResponse, number>({
            query: (nodeId) => ({
                url: `?node=${nodeId}`,
                method: 'POST',
                withCredentials: true,
            }),
            invalidatesTags: ['ODMRecords'],
        }),

        // GET /api/odm/ - Get ODM records with filters
        // Supports RTK Query polling for active status monitoring
        // Returns ODM records ordered by descending timestamp
        getActiveODMs: builder.query<ODMRecord[], GetODMsParams>({
            query: (params) => ({
                url: '',
                method: 'GET',
                withCredentials: true,
                params,
            }),
            providesTags: ['ODMRecords'],
        }),
    }),
});

export const { useCreateODMMutation, useGetActiveODMsQuery } = odmAPI;

export const odmAPIReducer = odmAPI.reducer;
export const odmAPIReducerPath = odmAPI.reducerPath;
export const odmAPIReducerMiddleware = odmAPI.middleware;
